import { Sparkles } from 'lucide-react';

import { cn } from '@/lib/cn';
import type { ITDirection } from '@/types/test';
import { DirectionIcon } from './DirectionIcon';
import { DIRECTION_STYLES } from './direction-styles';
import { RingProgress } from './RingProgress';

/**
 * Универсальная карточка-сводка профиля. Три режима:
 *  - 'preview'  (Welcome): header = имя + длинный info-text «Так выглядит
 *               часть результата…». Список 4 строк (моки).
 *  - 'partial'  (BetweenStages): header = имя + кольцо «50%» (доля заполнен-
 *               ности). Список 4 строк, поля Направление и Что попробовать
 *               показаны placeholder'ом «заполнится».
 *  - 'full'     (Result): header = имя + pill «✦ Сводка». Grid 2 серых
 *               блока «УРОВЕНЬ» / «БАЛЛ». Список 3 строк (без «Уровень»).
 *
 * Везде толстая 2px рамка accent-tint и горизонтальные разделители между
 * строками списка.
 */

const DIRECTION_LABELS: Record<ITDirection, string> = {
  PROG: 'Программирование',
  DESIGN: 'Дизайн',
  GAME: 'Игры',
  SITE: 'Сайты',
  MODEL: '3D-моделирование',
  WEB: 'Веб-разработка',
  SEC: 'Кибербезопасность',
  AI: 'ИИ',
  DATA: 'Данные',
};

export type SummaryMode = 'preview' | 'partial' | 'full';

interface ProfileSummaryCardProps {
  mode: SummaryMode;
  name: string;
  age: number;
  /** Уровень (Новичок/Любознательный/Уверенный/Продвинутый и т.п.). */
  level: string;
  /** Балл и максимум — для full-режима (отображается в боковом «БАЛЛ» блоке). */
  score?: { value: number; max: number };
  /** Сильные стороны как pills. До 3. */
  strengths: string[];
  /** Главное направление IT. null для partial — placeholder «заполнится». */
  topDirection: ITDirection | null;
  /** Что попробовать (1–3 направления). null для partial — «заполнится». */
  tryDirections: ITDirection[] | null;
  /** Доля заполненности для 'partial' (например 0.5 = 50%). */
  fillPercent?: number;
  className?: string;
}

export function ProfileSummaryCard({
  mode,
  name,
  age,
  level,
  score,
  strengths,
  topDirection,
  tryDirections,
  fillPercent = 0.5,
  className,
}: ProfileSummaryCardProps) {
  return (
    <article
      className={cn(
        'rounded-2xl border-4 p-4 bg-background',
        'border-border',
        className,
      )}
      aria-label="Сводка цифрового профиля"
    >
      <Header
        mode={mode}
        name={name}
        age={age}
        score={score}
        fillPercent={fillPercent}
      />

      {/* Для full — grid 2 серых блока УРОВЕНЬ/БАЛЛ под header'ом */}
      {mode === 'full' && score && (
        <div className="grid grid-cols-2 gap-2 mt-3">
          <MetricBox label="УРОВЕНЬ" value={level} valueClass="text-accent" />
          <MetricBox
            label="БАЛЛ"
            value={`${score.value} / ${score.max}`}
            valueClass="text-text-primary"
          />
        </div>
      )}

      <hr className="my-3 border-border" />

      <dl className="flex flex-col text-sm">
        {/* В full строка «Уровень» уже есть в grid — пропускаем */}
        {mode !== 'full' && (
          <Row label="Уровень">
            <span className="font-semibold text-accent">{level}</span>
          </Row>
        )}

        {/* Сильные стороны: на full-режиме без данных строку скрываем
            (у Новичков/Любознательных может не быть strong-блоков — это норма).
            На preview/partial — показываем placeholder. */}
        {(mode !== 'full' || strengths.length > 0) && (
          <Row label="Сильные стороны" wrap>
            {strengths.length > 0 ? (
              strengths.map((s) => <FilledPill key={s}>{s}</FilledPill>)
            ) : (
              <PlaceholderPill>заполнится</PlaceholderPill>
            )}
          </Row>
        )}

        {(() => {
          // На full-режиме без tryDirections строку «Что попробовать»
          // не показываем — тогда «Направление в IT» становится последней строкой.
          const hasTry = tryDirections && tryDirections.length > 0;
          const showTry = mode !== 'full' || hasTry;
          return (
            <>
              <Row label="Направление в IT" last={!showTry}>
                {topDirection ? (
                  <DirectionPill direction={topDirection} />
                ) : (
                  <PlaceholderPill>заполнится</PlaceholderPill>
                )}
              </Row>
              {showTry && (
                <Row label="Что попробовать" wrap last>
                  {hasTry ? (
                    tryDirections!.map((d) => <DirectionPill key={d} direction={d} />)
                  ) : (
                    <PlaceholderPill>заполнится</PlaceholderPill>
                  )}
                </Row>
              )}
            </>
          );
        })()}
      </dl>
    </article>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * Header — слева ЦИФРОВОЙ ПРОФИЛЬ + имя; справа info-text/ring/pill
 * ──────────────────────────────────────────────────────────────────────────── */

function Header({
  mode,
  name,
  age,
  score,
  fillPercent,
}: {
  mode: SummaryMode;
  name: string;
  age: number;
  score?: { value: number; max: number };
  fillPercent: number;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
        <span className="text-[11px] uppercase tracking-wide font-extrabold text-accent">
          Цифровой профиль
        </span>
        <span className="text-lg font-bold text-text-primary leading-tight">
          {name}, {age} лет
        </span>
      </div>

      {mode === 'preview' && (
        <p className="text-xs leading-snug text-text-secondary max-w-[55%]">
          Так выглядит часть результата. После теста — разбор по 5 направлениям и рекомендации.
        </p>
      )}

      {mode === 'partial' && (
        <RingProgress
          value={Math.round(fillPercent * 100)}
          max={100}
          center={`${Math.round(fillPercent * 100)}%`}
          size={64}
        />
      )}

      {mode === 'full' && score && (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-text-secondary">
          <Sparkles size={14} strokeWidth={1.75} />
          Сводка
        </span>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * Подкомпоненты
 * ──────────────────────────────────────────────────────────────────────────── */

function MetricBox({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <div className="rounded-xl bg-surface px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-wide font-semibold text-text-secondary">
        {label}
      </div>
      <div className={cn('text-base font-bold leading-tight mt-0.5', valueClass)}>
        {value}
      </div>
    </div>
  );
}

function Row({
  label,
  children,
  wrap,
  last,
}: {
  label: string;
  children: React.ReactNode;
  wrap?: boolean;
  last?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-3 py-2.5',
        !last && 'border-b border-border',
      )}
    >
      <dt className="text-text-secondary shrink-0 self-center">{label}</dt>
      <dd
        className={cn(
          'flex items-center justify-end gap-1.5',
          wrap && 'flex-wrap',
        )}
      >
        {children}
      </dd>
    </div>
  );
}

function FilledPill({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
      {children}
    </span>
  );
}

function PlaceholderPill({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full bg-text-secondary/10 px-2.5 py-0.5 text-xs font-medium text-text-secondary">
      {children}
    </span>
  );
}

function DirectionPill({ direction }: { direction: ITDirection }) {
  const style = DIRECTION_STYLES[direction];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium',
        style ? `${style.bgClass} ${style.fgClass}` : 'bg-primary/10 text-primary',
      )}
    >
      <DirectionIcon
        direction={direction}
        size="sm"
        className="!h-5 !w-5 rounded-md"
      />
      {DIRECTION_LABELS[direction]}
    </span>
  );
}
